import type { BgMove, BgView } from "@bing/engine-backgammon";
import {
  BACKGAMMON_ONLINE_ROOM,
  BACKGAMMON_SHORT_MATCH_LENGTH,
  BACKGAMMON_SHORT_ONLINE_ROOM,
} from "@bing/shared";
import { useState, type ReactNode } from "react";
import { fa } from "../i18n/fa";
import { BackgammonColorContext, BackgammonTable } from "../screens/BackgammonTable";
import { getBackgammonColor, getBackgammonPublicMatchTo } from "../settings/prefs";
import { useSession } from "../store/context";
import { BackgammonEndOverlay } from "./BackgammonGame";
import { OnlineGame } from "./OnlineGame";
import { otherHuman } from "./seat-identity";

/** Online تخته نرد — a thin wrapper over the shared `OnlineGame` shell (public quick-match OR a host's
 * private table). 2-seat, perfect-information: no CardDeck, no team overlay. Threads self + opponent
 * identity and the per-turn window — تخته نرد's only clock (the whole-game time bank was
 * removed 2026-08-15). */
export function BackgammonOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  /*
   * The winning checker must be SEEN coming off before the end card covers the board. The shared
   * `OnlineGame` shell owns its own reveal beat (1.4s) and is used by every game, so rather than
   * reach into it for one game, the table reports when its replay is still draining and `renderWin`
   * simply withholds the overlay until it settles. The shell keeps calling `renderWin` as we
   * re-render, so the card appears the moment the last checker has landed.
   */
  const [replayDraining, setReplayDraining] = useState(false);
  /*
   * TWO PUBLIC ROOM TYPES, so «۵ or ۳ دست» picks the QUEUE, not a setting.
   *
   * A quick-match table has nobody to choose for the stranger who joins it, and this branch sends
   * no settings at all (the server ignores them there outright); `maxClients`/name are fixed at
   * `define()` time, so a room type IS its matchmaking queue. Somebody who tapped «اتاق عمومی»
   * must never be seated at a three-point table, which is exactly why the two cannot share one
   * room. The server FORCES the length from whichever room you land in — this only chooses which.
   *
   * تخته نرد was the first game where a public room still offers a choice (operator, 2026-08-27);
   * پاسور's table for two is the second (2026-10-20), a SIZE where this is a length.
   * The lobby asks it as a fourth BUTTON on the mode sheet, حکم's shape — never a second step.
   *
   * A HOST still picks any of 1/3/5/7 in «تنظیمات میز»; that is a different pref entirely.
   */
  const room =
    getBackgammonPublicMatchTo() === BACKGAMMON_SHORT_MATCH_LENGTH
      ? BACKGAMMON_SHORT_ONLINE_ROOM
      : BACKGAMMON_ONLINE_ROOM;
  return (
    <OnlineGame<BgMove, BgView>
      gameId="backgammon"
      room={room}
      /* ⚠️ STATED, NEVER DERIVED. `OnlineGame` otherwise swaps `-online` → `-private`, which on the
         short room asks for a `backgammon3-private` the server registers nowhere — «با دوستام»
         would then break for exactly the players whose last public tap was «کوتاه». There is ONE
         private تخته نرد room type; a host picks the length in «تنظیمات میز». */
      privateRoom="backgammon-private"
      title={fa.backgammon.title}
      renderTable={({ view, legal, send, leave, players, timer }) => {
        return (
          /* ITEM 12 — the same viewer-side checker colour the offline game uses. Each player
             sees THEIR OWN checkers in the colour they picked, so two players who both chose
             gold still agree on the position — only the palette differs per screen. */
          <BackgammonColorContext.Provider value={getBackgammonColor()}>
            <BackgammonTable
              view={view}
              legal={legal}
              onMove={send}
              onLeave={leave}
              self={profile}
              opponent={otherHuman(players, view.you)}
              /* The PER-TURN ring. The shell already computes this for every game; backgammon
                 simply never received one because the server did not arm a turn timer for it. The
                 moment it does, the ring appears here with no further client change — and until
                 then `timer` is null and the panels just state the turn in words. */
              timer={
                timer
                  ? {
                      endsAt: timer.turn.endsAt,
                      seat: timer.turn.seat,
                      totalSec: timer.totalSec,
                      // How long THIS window is, straight from the server's frame — a تخته نرد
                      // DOUBLE stretches the turn to 1.5× the setting, so the ring must never assume
                      // the configured length (assuming it is what made the ring look frozen).
                      totalMs: timer.turn.totalMs,
                    }
                  : null
              }
              onDraining={setReplayDraining}
            />
          </BackgammonColorContext.Provider>
        );
      }}
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        againDisabled,
        againHint,
        againHidden,
      }) => {
        if (replayDraining) return null;
        return (
          <BackgammonEndOverlay
            result={result}
            view={view}
            self={profile}
            opponent={otherHuman(players, view.you)}
            onAgain={again}
            againDisabled={againDisabled}
            againHint={againHint}
            againHidden={againHidden}
            onLeave={leave}
          />
        );
      }}
    />
  );
}
